import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext.js';
import { api } from '../lib/api.js';
import { usePrefs, APP_VERSION, type Background, type Language } from '../lib/prefs.js';
import { useT } from '../lib/i18n.js';
import { AvatarLab } from './AvatarLab.js';
import { PersonaPane } from './PersonaPane.js';
import { PeoplePane } from './PeoplePane.js';
import { LegacyPane } from './LegacyPane.js';
import { AnalysisPane } from './AnalysisPane.js';
import { NewsPane } from './NewsPane.js';
import { LearningPane } from './LearningPane.js';
import { UsagePane } from './UsagePane.js';
import { ActivityLogPane } from './ActivityLogPane.js';

type Tab = 'profile' | 'persona' | 'people' | 'organizations' | 'legacy' | 'news' | 'analysis' | 'learning' | 'costs' | 'activity' | 'appearance' | 'llm' | 'servers' | 'avatar' | 'database';

interface Props { open: boolean; onClose: () => void; variant?: 'modal' | 'page' }

// Two-level IA: groups (top tabs) → items (left list). 15 flat tabs were hard to
// navigate; grouping by concern makes the admin scannable.
const GROUPS: { id: string; label: string; tabs: Tab[] }[] = [
  { id: 'you', label: 'You', tabs: ['profile', 'appearance'] },
  { id: 'twin', label: 'Twin', tabs: ['persona', 'avatar', 'learning', 'analysis', 'news'] },
  { id: 'people', label: 'People', tabs: ['people', 'organizations', 'legacy'] },
  { id: 'engine', label: 'Engine', tabs: ['llm', 'servers'] },
  { id: 'system', label: 'System', tabs: ['costs', 'activity', 'database'] },
];
const VISITOR_TABS = new Set<Tab>(['profile', 'appearance']);

export function SettingsModal({ open, onClose, variant = 'modal' }: Props) {
  const { user } = useAuth();
  const tr = useT();
  const [tab, setTab] = useState<Tab>('profile');

  // Visitors (not owner/trustee) only get their profile + personal appearance.
  const privileged = user?.role === 'owner' || user?.role === 'trustee';
  const visibleGroups = GROUPS
    .map((g) => ({ ...g, tabs: g.tabs.filter((t) => privileged || VISITOR_TABS.has(t)) }))
    .filter((g) => g.tabs.length > 0);

  // Active group is derived from the active tab (no separate state to keep in sync).
  const activeGroup = visibleGroups.find((g) => g.tabs.includes(tab)) ?? visibleGroups[0]!;

  // Keep the active tab within what this role may see.
  useEffect(() => {
    if (!visibleGroups.some((g) => g.tabs.includes(tab))) setTab(visibleGroups[0]?.tabs[0] ?? 'profile');
  }, [visibleGroups, tab]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const cardClass = variant === 'page'
    ? 'w-full max-w-[1200px] mx-auto h-[calc(100dvh-7rem)] rounded-2xl bg-void border border-ink/12 grid grid-rows-[auto_1fr] overflow-hidden'
    : 'w-full h-dvh rounded-none md:w-[min(1000px,94vw)] md:h-[min(720px,92vh)] md:rounded-2xl bg-void border border-ink/15 grid grid-rows-[auto_1fr] overflow-hidden shadow-2xl';

  const card = (
      <div
        onClick={(e) => e.stopPropagation()}
        className={cardClass}
      >
        <header className="px-5 md:px-6 pt-safe md:pt-5 border-b border-ink/10">
          <div className="flex items-center justify-between py-4 md:py-4">
            <span className="font-serif text-xl text-ink">Admin console</span>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full border border-ink/15 grid place-items-center text-ink/50 hover:text-ink hover:border-ink/40"
              aria-label="Close"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          {/* Group tabs (top level) */}
          {visibleGroups.length > 1 && (
            <div className="flex gap-1 overflow-x-auto -mb-px">
              {visibleGroups.map((g) => {
                const on = g.id === activeGroup.id;
                return (
                  <button key={g.id} onClick={() => { if (!g.tabs.includes(tab)) setTab(g.tabs[0]!); }}
                    className={`px-3.5 py-2 text-[13px] whitespace-nowrap border-b-2 transition-colors ${
                      on ? 'border-amber text-ink' : 'border-transparent text-ink/45 hover:text-ink/75'
                    }`}>
                    {g.label}
                  </button>
                );
              })}
            </div>
          )}
        </header>

        <div className="grid grid-rows-[auto_1fr] md:grid-rows-1 md:grid-cols-[200px_1fr] min-h-0">
          {/* Item nav (within the active group) — vertical on desktop, strip on mobile */}
          <nav className="flex md:flex-col gap-0.5 overflow-x-auto md:overflow-x-visible px-3 md:px-4 py-2 md:py-5 border-b md:border-b-0 md:border-r border-ink/10 shrink-0">
            {activeGroup.tabs.map((id) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`px-3 py-2 md:py-2 text-left rounded-lg text-[13px] whitespace-nowrap transition-colors ${
                  tab === id
                    ? 'text-ink bg-amber/[0.10] md:shadow-[inset_2px_0_0] shadow-amber'
                    : 'text-ink/50 hover:text-ink/80 hover:bg-white/[0.02]'
                }`}
              >
                {tr('settings.tab.' + id)}
              </button>
            ))}
            <div className="hidden md:block mt-auto pt-4 border-t border-ink/10">
              <div className="text-[10px] text-ink/30 mb-1.5">Encrypted · Local</div>
              <div className="font-mono text-[9.5px] text-ink/40 leading-relaxed">
                AES-256-GCM<br />
                martindaigneault.ai<br />
                {APP_VERSION}
              </div>
            </div>
          </nav>
          <main className="px-5 md:px-8 py-5 md:py-6 overflow-y-auto pb-safe">
            {user?.role === 'trustee' && tab !== 'profile' && tab !== 'appearance' && (
              <div className="mb-4 px-3 py-2 rounded-lg border border-amber/30 bg-amber/[0.05] text-[11.5px] text-amber/85">
                Read-only — as a trusted person you can view everything; only the owner can make changes.
              </div>
            )}
            {tab === 'profile' && <ProfilePane />}
            {tab === 'persona' && <PersonaPane />}
            {tab === 'people' && <PeoplePane />}
            {tab === 'organizations' && <OrgsPane />}
            {tab === 'legacy' && <LegacyPane />}
            {tab === 'appearance' && <AppearancePane />}
            {tab === 'llm' && <LLMPane />}
            {tab === 'servers' && <ServersPane />}
            {tab === 'news' && <NewsPane />}
            {tab === 'analysis' && <AnalysisPane />}
            {tab === 'learning' && <LearningPane />}
            {tab === 'costs' && <UsagePane />}
            {tab === 'activity' && <ActivityLogPane />}
            {tab === 'avatar' && <AvatarLab />}
            {tab === 'database' && <DatabasePane />}
          </main>
        </div>
      </div>
  );

  // Desktop full-page (rendered inside the app shell main); modal otherwise.
  if (variant === 'page') return <div className="px-4 md:px-8">{card}</div>;
  return (
    <div className="fixed inset-0 z-[100] bg-black/65 backdrop-blur-md grid place-items-center animate-[fadeIn_240ms_ease]" onClick={onClose}>
      {card}
    </div>
  );
}

/* ============================ Shared atoms ============================ */

function PaneHead({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="mb-5">
      <h3 className="font-serif text-2xl text-ink m-0">{title}</h3>
      <p className="font-serif italic text-sm text-ink/55 mt-1 max-w-prose">{sub}</p>
    </div>
  );
}

function Row({ label, children, hint }: { label: string; children: ReactNode; hint?: ReactNode }) {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-5 items-start py-3 border-b border-ink/[0.06]">
      <div className="text-[11px] text-ink/45 pt-1">{label}</div>
      <div>
        <div className="font-serif text-[15px] text-ink">{children}</div>
        {hint && <div className="font-mono text-[9.5px] text-ink/40 mt-1">{hint}</div>}
      </div>
    </div>
  );
}

function StatusChip({ kind, children }: { kind: 'live' | 'queued' | 'draft' | 'off'; children: ReactNode }) {
  const colors: Record<typeof kind, string> = {
    live: 'bg-emerald-400/10 text-emerald-300',
    queued: 'bg-amber/15 text-amber',
    draft: 'bg-amber/10 text-amber/80',
    off: 'bg-ink/10 text-ink/50',
  };
  return (
    <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${colors[kind]}`}>
      {children}
    </span>
  );
}

function Toggle({ on, onChange }: { on: boolean; onChange?: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange?.(!on)}
      className={`w-9 h-5 rounded-full relative transition-colors ${on ? 'bg-amber/40' : 'bg-ink/15'}`}
    >
      <span
        className={`absolute top-0.5 w-3.5 h-3.5 rounded-full transition-all ${
          on ? 'left-[18px] bg-particle shadow-[0_0_6px] shadow-particle' : 'left-0.5 bg-ink/60'
        }`}
      />
    </button>
  );
}

/* ============================ Editable field ============================ */

function useSettingsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Record<string, string | string[] | boolean | number | null>) =>
      api<{ ok: boolean; applied: string[] }>('/api/admin/settings', {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'config'] });
    },
  });
}

// Speaking-rate slider with a live sample player. Hear the voice at the chosen
// speed before saving it. Owner-only (the pane is already owner-gated).
function TtsSpeedRow({ initial, onSave }: { initial: number; onSave: (v: number) => void }) {
  const [speed, setSpeed] = useState(initial ?? 1.0);
  const [playing, setPlaying] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const dirty = Math.abs(speed - (initial ?? 1.0)) > 0.001;

  async function sample() {
    setMsg(null); setPlaying(true);
    try {
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('mdai_token') : null;
      const r = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/tts/sample`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ speed }),
      });
      if (!r.ok) { setMsg(r.status === 409 ? 'Voix ElevenLabs non configurée' : 'Échec de la synthèse'); setPlaying(false); return; }
      const url = URL.createObjectURL(await r.blob());
      const audio = new Audio(url);
      const done = () => { URL.revokeObjectURL(url); setPlaying(false); };
      audio.onended = done; audio.onerror = done;
      await audio.play();
    } catch { setMsg('Erreur réseau'); setPlaying(false); }
  }

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <input type="range" min={0.7} max={1.2} step={0.05} value={speed}
        onChange={(e) => setSpeed(Number(e.target.value))}
        className="w-40 accent-amber" />
      <span className="font-mono text-[12px] text-ink/70 w-12">{speed.toFixed(2)}×</span>
      <button onClick={sample} disabled={playing}
        className="px-3 py-1 rounded-full border border-ink/20 text-[11.5px] text-ink/60 hover:text-ink disabled:opacity-40">
        {playing ? '▶ …' : '▶ Écouter'}
      </button>
      {dirty && (
        <button onClick={() => onSave(speed)}
          className="px-3 py-1 rounded-full bg-amber text-void text-[11.5px] hover:bg-particle">Enregistrer</button>
      )}
      {msg && <span className="text-[11px] text-rose-300/80">{msg}</span>}
    </div>
  );
}

function EditableText({
  initial,
  placeholder,
  type = 'text',
  onSave,
  sensitive,
  hint,
}: {
  initial: string | null;
  placeholder?: string;
  type?: 'text' | 'password';
  onSave: (next: string) => void;
  sensitive?: boolean;
  hint?: ReactNode;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  return editing ? (
    <div className="flex items-center gap-2">
      <input
        autoFocus
        type={sensitive ? 'password' : type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value)}
        className="flex-1 px-3 py-1.5 rounded-md border border-ink/20 bg-white/[0.02] font-mono text-[12px] text-ink focus:border-ink/50 focus:outline-none"
      />
      <button
        onClick={() => { onSave(value); setEditing(false); setValue(''); }}
        className="px-3 py-1.5 rounded-md bg-amber text-void text-[11px] hover:bg-particle"
      >Save</button>
      <button
        onClick={() => { setEditing(false); setValue(''); }}
        className="px-2 py-1.5 rounded-md text-[11px] text-ink/40 hover:text-ink/80"
      >Cancel</button>
    </div>
  ) : (
    <div className="flex items-center gap-3">
      <span className="font-mono text-[13px] text-ink">
        {initial !== null && initial !== '' ? initial : <span className="text-ink/30 italic font-serif">— not set —</span>}
      </span>
      <button
        onClick={() => setEditing(true)}
        className="px-2.5 py-0.5 rounded-full border border-ink/20 text-[11px] text-ink/50 hover:text-ink hover:border-ink/40"
      >
        Edit
      </button>
      {hint}
    </div>
  );
}

/* ============================ Profile ============================ */

function ProfilePane() {
  const { user, logout } = useAuth();
  return (
    <>
      <PaneHead title="Profile" sub="Your owner account. Change credentials by editing .env then running `npm run db:set-owner`." />
      <Row label="Display name">{user?.name ?? '—'}</Row>
      <Row label="Email">{user?.email ?? '—'}</Row>
      <Row label="Role"><span className="font-mono text-particle text-[13px]">{user?.role}</span></Row>
      <Row label="Tier"><span className="font-mono text-[13px]">{user?.tier?.replace('_', ' ')}</span></Row>
      <Row label="Session">JWT in localStorage · prefs synced to DB</Row>
      <div className="mt-6">
        <button
          onClick={logout}
          className="px-4 py-2 rounded-full border border-ink/20 text-[11.5px] text-ink/60 hover:text-ink hover:border-ink/40"
        >
          Sign out
        </button>
      </div>
    </>
  );
}

/* ============================ Organizations ============================ */

interface Org { id: string; name: string; slug: string; emailDomain: string | null; hasProKey: boolean; voiceId: string | null; avatarPhotoId: string | null; monthlyCapUsd: number | null; memberCount: number }
interface AvatarPhotoLite { id: string; fileName: string }

function OrgsPane() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<{ organizations: Org[] }>({ queryKey: ['organizations'], queryFn: () => api('/api/organizations') });
  const photosQ = useQuery<{ photos: AvatarPhotoLite[] }>({ queryKey: ['avatar', 'photos'], queryFn: () => api('/api/avatar/photos') });
  const photos = photosQ.data?.photos ?? [];
  const [name, setName] = useState(''); const [domain, setDomain] = useState(''); const [key, setKey] = useState(''); const [voice, setVoice] = useState(''); const [busy, setBusy] = useState(false);

  async function create() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api('/api/organizations', { method: 'POST', body: JSON.stringify({ name, emailDomain: domain || undefined, proApiKey: key || undefined, voiceId: voice || undefined }) });
      setName(''); setDomain(''); setKey(''); setVoice(''); qc.invalidateQueries({ queryKey: ['organizations'] });
    } finally { setBusy(false); }
  }
  async function patch(id: string, body: Record<string, string | number | null>) {
    await api(`/api/organizations/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
    qc.invalidateQueries({ queryKey: ['organizations'] });
  }
  async function del(id: string) {
    if (!window.confirm('Delete this organization? Its members will be unlinked.')) return;
    await api(`/api/organizations/${id}`, { method: 'DELETE' });
    qc.invalidateQueries({ queryKey: ['organizations'] });
  }
  const inp = 'px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-serif text-[14px] text-ink focus:border-ink/40 focus:outline-none';

  return (
    <>
      <PaneHead title="Organizations" sub="Companies whose colleagues use the professional twin. Each org brings its own Anthropic key — it pays for its members’ usage." />
      <div className="rounded-xl border border-ink/10 p-4 mb-5 grid grid-cols-1 gap-2">
        <div className="grid grid-cols-2 gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="company name (e.g. Siemens)" className={inp} />
          <input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="email domain (siemens.com)" className={inp} />
        </div>
        <input value={key} onChange={(e) => setKey(e.target.value)} type="password" autoComplete="off" placeholder="Pro API key (Anthropic) — optional, billed to the company" className={inp} />
        <input value={voice} onChange={(e) => setVoice(e.target.value)} placeholder="ElevenLabs voice id — optional, this org's twin voice" className={inp} />
        <button onClick={create} disabled={busy || !name.trim()}
          className="self-start px-4 py-2 rounded-full bg-amber text-void text-[11.5px] hover:bg-particle disabled:opacity-30">
          {busy ? '…' : 'Add organization'}
        </button>
      </div>

      {isLoading ? <Loader /> : (data?.organizations.length ?? 0) === 0 ? (
        <p className="font-serif italic text-ink/40 text-sm">No organizations yet.</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {data!.organizations.map((o) => (
            <li key={o.id} className="flex flex-col gap-2 py-2.5 px-3 rounded-lg border border-ink/[0.06]">
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-[14px] text-ink truncate">{o.name}
                    <span className="text-ink/40 text-[12px]"> · {o.memberCount} member{o.memberCount === 1 ? '' : 's'}</span></div>
                  <div className="text-[11px] text-ink/40">
                    {o.emailDomain || 'no domain'} · {o.hasProKey ? <span className="text-emerald-300/80">● pro key</span> : <span className="text-ink/30">○ no key</span>}
                    {o.voiceId ? <span className="text-sky-300/70"> · ♪ voice</span> : ''}
                    {o.avatarPhotoId ? <span className="text-particle"> · ◐ avatar</span> : ''}
                    {o.monthlyCapUsd != null ? <span className="text-amber/70"> · cap ${o.monthlyCapUsd}</span> : ''}
                  </div>
                </div>
                <EditableText initial={o.voiceId} placeholder="voice id" onSave={(v) => patch(o.id, { voiceId: v || null })} />
                <EditableText initial={o.hasProKey ? '••••••••' : null} placeholder="set pro key" sensitive onSave={(v) => patch(o.id, { proApiKey: v || null })} />
                <button onClick={() => del(o.id)} title="Delete organization" className="text-ink/40 hover:text-red-300 px-1 text-[13px]">🗑</button>
              </div>
              <div className="flex items-center gap-2 flex-wrap pl-1">
                <span className="text-[11px] text-ink/35">Avatar</span>
                <select value={o.avatarPhotoId ?? ''} onChange={(e) => patch(o.id, { avatarPhotoId: e.target.value || null })}
                  className="px-1.5 py-1 rounded border border-ink/15 bg-void text-[11px] text-ink/70 max-w-[140px]">
                  <option value="">global default</option>
                  {photos.map((p) => <option key={p.id} value={p.id}>{p.fileName.slice(0, 18)}</option>)}
                </select>
                <span className="text-[11px] text-ink/35 ml-2">Cap $/mo</span>
                <input type="number" min={0} step={1} defaultValue={o.monthlyCapUsd ?? ''} key={`cap-${o.monthlyCapUsd}`}
                  onBlur={(e) => { const v = e.target.value.trim(); patch(o.id, { monthlyCapUsd: v === '' ? null : Number(v) }); }}
                  placeholder="none"
                  className="w-20 px-2 py-1 rounded border border-ink/15 bg-void font-mono text-[11px] text-ink" />
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/* ============================ Appearance ============================ */

function AppearancePane() {
  const [prefs, setPrefs] = usePrefs();
  const BG_DESCS: Record<Background, string> = {
    void: 'Pure black with a gold glow — the signature.',
    amethyst: 'Deep black with a violet glow.',
    daylight: 'High-contrast light mode.',
  };
  return (
    <>
      <PaneHead title="Appearance" sub="Visual preferences. Synced to your account — same across browsers." />

      <Row label="Background" hint={BG_DESCS[prefs.background]}>
        <div className="flex gap-2 flex-wrap">
          {(Object.keys(BG_DESCS) as Background[]).map((b) => (
            <button
              key={b}
              onClick={() => setPrefs({ background: b })}
              className={`px-3 py-1.5 rounded-full border text-[11.5px] transition-colors ${ prefs.background === b ? 'bg-amber text-void border-amber' : 'border-ink/15 text-ink/60 hover:text-ink hover:border-ink/40' }`}
            >
              {b}
            </button>
          ))}
        </div>
      </Row>

      <Row label="Language" hint="Sets the interface language and the persona reply / intake question language.">
        <div className="flex gap-2">
          {(['fr', 'en', 'es'] as Language[]).map((l) => (
            <button
              key={l}
              onClick={() => setPrefs({ language: l })}
              className={`px-3 py-1.5 rounded-full border text-[11.5px] ${ prefs.language === l ? 'bg-ink text-void border-ink' : 'border-ink/15 text-ink/60' }`}
            >
              {l === 'fr' ? 'Français (QC)' : l === 'en' ? 'English' : 'Español'}
            </button>
          ))}
        </div>
      </Row>

      <Row label="Particle density" hint="More particles = slower on older machines.">
        <div className="flex gap-2">
          {(['low', 'medium', 'high'] as const).map((d) => (
            <button
              key={d}
              onClick={() => setPrefs({ particleDensity: d })}
              className={`px-3 py-1.5 rounded-full border text-[11.5px] ${ prefs.particleDensity === d ? 'bg-ink text-void border-ink' : 'border-ink/15 text-ink/60' }`}
            >
              {d}
            </button>
          ))}
        </div>
      </Row>

      <Row label="Cinematic mode" hint="Hide chrome during conversation; voice + avatar only.">
        <Toggle on={prefs.cinematicMode} onChange={(v) => setPrefs({ cinematicMode: v })} />
      </Row>

      <Row label="Reduce motion" hint="Calmer particle drift, fewer animations.">
        <Toggle on={prefs.reduceMotion} onChange={(v) => setPrefs({ reduceMotion: v })} />
      </Row>
    </>
  );
}

/* ============================ Config-driven panes ============================ */

interface ConfigResp {
  llm: { mode: 'api' | 'mcp'; provider: string; model: string; modelCheap: string; apiKeyMasked: string | null; apiKeyPresent: boolean; webSearch: boolean };
  voice: {
    stt: { url: string | null; keyPresent: boolean };
    tts: { provider: string | null; voiceId: string | null; model: string | null; speed: number; keyPresent: boolean };
  };
  news: { feeds: string[] };
  server: { env: string; port: number; nodeVersion: string; uptimeMs: number };
  auth: { jwtExpiresIn: string; jwtSecretLooksWeak: boolean };
}

function useConfig() {
  return useQuery<ConfigResp>({
    queryKey: ['admin', 'config'],
    queryFn: () => api<ConfigResp>('/api/admin/config'),
    refetchInterval: 10_000,
  });
}

function ModelPicker({ current, onSave }: { current: string | null; onSave: (v: string) => void }) {
  const { data } = useQuery<{ models: { id: string; display: string }[]; configured: boolean; error?: string }>({
    queryKey: ['admin', 'models'],
    queryFn: () => api('/api/admin/models'),
    staleTime: 60_000,
  });
  const [custom, setCustom] = useState(false);
  const cur = current ?? '';

  if (!data) return <span className="font-mono text-[12px] text-ink/40">{cur || 'claude-sonnet-4-5'}</span>;

  const models = data.models ?? [];
  if (models.length === 0) {
    // No key (or API error) → keep a free-text field.
    return (
      <div className="flex flex-col gap-1">
        <EditableText initial={current} placeholder="claude-sonnet-4-5" onSave={onSave} />
        {data.error && <span className="font-mono text-[9px] text-amber/70">{data.error}</span>}
      </div>
    );
  }
  const knownIds = models.map((mm) => mm.id);
  const isCustom = custom || (!!cur && !knownIds.includes(cur));

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <select
        value={isCustom ? '__custom' : cur}
        onChange={(e) => {
          if (e.target.value === '__custom') { setCustom(true); return; }
          setCustom(false); onSave(e.target.value);
        }}
        className="px-3 py-1.5 rounded-lg border border-ink/15 bg-white/[0.02] font-mono text-[12px] text-ink focus:border-ink/40 focus:outline-none">
        {!cur && <option value="">— default (claude-sonnet-4-5) —</option>}
        {models.map((mm) => <option key={mm.id} value={mm.id}>{mm.display}</option>)}
        <option value="__custom">Custom…</option>
      </select>
      {isCustom && <EditableText initial={current} placeholder="model id" onSave={onSave} />}
    </div>
  );
}

function RevokeSessionsButton() {
  const { logout } = useAuth();
  const [busy, setBusy] = useState(false);
  return (
    <button disabled={busy}
      onClick={async () => {
        if (!confirm('Invalidate all sessions on every device? You will be logged out and must sign in again.')) return;
        setBusy(true);
        try { await api('/api/auth/revoke-all', { method: 'POST' }); } catch { /* ignore */ }
        logout();
      }}
      className="px-3.5 py-1.5 rounded-full border border-red-300/40 text-[11.5px] text-red-300/80 hover:text-red-300 disabled:opacity-40">
      {busy ? '…' : 'Revoke all sessions'}
    </button>
  );
}

function LLMPane() {
  const { data, isLoading } = useConfig();
  const m = useSettingsMutation();
  return (
    <>
      <PaneHead title="Language model" sub="Edits write to the database immediately (encrypted for secrets). No restart needed." />
      {isLoading || !data ? (
        <Loader />
      ) : (
        <>
          <Row label="Engine" hint={data.llm.mode === 'api'
            ? 'Server calls the Anthropic API (uses your key + budget).'
            : 'Server does NOT call the API. Generation runs through Claude Desktop (MCP tools) at zero cost — the dev default.'}>
            <div className="flex gap-1.5">
              {(['mcp', 'api'] as const).map((mode) => (
                <button key={mode} onClick={() => m.mutate({ 'llm.mode': mode })}
                  className={`px-3 py-1.5 rounded-full text-[11.5px] border transition-colors ${ data.llm.mode === mode ? 'bg-amber text-void border-amber' : 'border-ink/15 text-ink/55 hover:text-ink' }`}>
                  {mode === 'mcp' ? 'Claude Desktop' : 'API'}
                </button>
              ))}
            </div>
          </Row>
          <Row label="Provider">
            <span className="font-mono text-[13px]">{data.llm.mode === 'api' ? data.llm.provider : 'claude desktop (mcp)'}</span>{' '}
            {data.llm.mode === 'api'
              ? (data.llm.apiKeyPresent ? <StatusChip kind="live">live</StatusChip> : <StatusChip kind="off">no key</StatusChip>)
              : <StatusChip kind="live">mcp</StatusChip>}
          </Row>
          <Row label="Model" hint="Main model — quality-critical work (conversation, analysis, persona). Opus is ~5× Sonnet; Sonnet is the sweet spot.">
            <ModelPicker current={data.llm.model} onSave={(v) => m.mutate({ 'anthropic.model': v || null })} />
          </Row>
          <Row label="Cheap model" hint="Low-stakes, high-frequency calls (follow-up questions, typo fixes, tier classification, reflection topics). Default Haiku — keeps cost down.">
            <ModelPicker current={data.llm.modelCheap} onSave={(v) => m.mutate({ 'anthropic.modelCheap': v || null })} />
          </Row>
          <Row label="Web search" hint={data.llm.webSearch
            ? 'The twin can search the web in real time to answer (Anthropic web search ~$0.01/search, API mode).'
            : 'Off — the twin answers only from the vault + persona.'}>
            <Toggle on={data.llm.webSearch} onChange={(v) => m.mutate({ 'llm.webSearch': v })} />
          </Row>
          <Row label="API key" hint={data.llm.apiKeyPresent ? 'Encrypted at rest with AES-256-GCM' : 'Set this to enable real persona replies.'}>
            <EditableText
              initial={data.llm.apiKeyMasked}
              placeholder="sk-ant-..."
              sensitive
              onSave={(v) => m.mutate({ 'anthropic.apiKey': v || null })}
            />
          </Row>
          <Row label="Session expiry" hint="How long a login stays valid before re-auth is required. Short by design (confidential data) — default 4h. e.g. 4h, 8h, 1d.">
            <EditableText
              initial={data.auth.jwtExpiresIn}
              placeholder="4h"
              onSave={(v) => m.mutate({ 'auth.jwtExpiresIn': v || null })}
            />
          </Row>
          <Row label="Sessions" hint="Invalidate every active session everywhere (use if a device or token may be compromised). You'll be logged out too.">
            <RevokeSessionsButton />
          </Row>
          {data.auth.jwtSecretLooksWeak && (
            <div className="mt-4 px-3 py-2 border-l-2 border-amber bg-amber/[0.04] font-serif italic text-[13px] text-ink/70">
              ⚠ JWT_SECRET is missing or short. Generate a 64-char random string and put it in .env before exposing the server. (Rotating JWT_SECRET invalidates encrypted settings — you'll need to re-enter API keys.)
            </div>
          )}
        </>
      )}
    </>
  );
}

function ServersPane() {
  const { data, isLoading } = useConfig();
  const m = useSettingsMutation();
  return (
    <>
      <PaneHead title="Infrastructure" sub="Where the pieces run, and the external services they talk to." />
      {isLoading || !data ? (
        <Loader />
      ) : (
        <>
          <Row label="Frontend"><span className="font-mono text-[13px]">localhost:5173 (Vite dev)</span></Row>
          <Row label="Backend">
            <span className="font-mono text-[13px]">localhost:{data.server.port}</span>{' '}
            <StatusChip kind="live">live</StatusChip>
            <span className="ml-2 font-mono text-[10.5px] text-ink/40">{data.server.env}</span>
          </Row>
          <Row label="Node">{data.server.nodeVersion}</Row>
          <Row label="Uptime">{formatUptime(data.server.uptimeMs)}</Row>

          <Row label="STT endpoint" hint="Whisper-compatible API URL.">
            <EditableText
              initial={data.voice.stt.url}
              placeholder="https://api.openai.com/v1"
              onSave={(v) => m.mutate({ 'whisper.url': v || null })}
            />
          </Row>
          <Row label="STT key">
            <EditableText
              initial={data.voice.stt.keyPresent ? '••••••••' : null}
              placeholder="sk-..."
              sensitive
              onSave={(v) => m.mutate({ 'whisper.apiKey': v || null })}
            />
          </Row>

          <Row label="TTS provider">
            {data.voice.tts.provider ? <span className="font-mono text-[13px]">{data.voice.tts.provider}</span> : <StatusChip kind="off">not configured</StatusChip>}
          </Row>
          <Row label="TTS voice id">
            <EditableText
              initial={data.voice.tts.voiceId}
              placeholder="MD-001"
              onSave={(v) => m.mutate({ 'elevenlabs.voiceId': v || null })}
            />
          </Row>
          <Row label="TTS key">
            <EditableText
              initial={data.voice.tts.keyPresent ? '••••••••' : null}
              placeholder="sk-..."
              sensitive
              onSave={(v) => m.mutate({ 'elevenlabs.apiKey': v || null })}
            />
          </Row>
          <Row label="TTS model" hint="Blank = eleven_multilingual_v2. eleven_v4_turbo = best real-time (~100ms); eleven_turbo_v2_5 also fast.">
            <EditableText
              initial={data.voice.tts.model}
              placeholder="eleven_v4_turbo"
              onSave={(v) => m.mutate({ 'elevenlabs.model': v || null })}
            />
          </Row>
          <Row label="TTS speed" hint="Débit de la voix : 0.7 plus lent · 1.0 normal · 1.2 plus rapide. Écoute un échantillon avant d'enregistrer.">
            <TtsSpeedRow initial={data.voice.tts.speed} onSave={(v) => m.mutate({ 'elevenlabs.speed': v })} />
          </Row>

          <Row label="News feeds" hint="Comma-separated RSS URLs. Used to seed daily questions.">
            <EditableText
              initial={data.news.feeds.join(', ')}
              placeholder="https://www.ledevoir.com/rss, ..."
              onSave={(v) => m.mutate({ 'news.feeds': v ? v.split(',').map((s) => s.trim()).filter(Boolean) : null })}
            />
          </Row>
          <Row label="Login intro video" hint="Shown above the sign-in form. MP4/WebM.">
            <IntroVideoControl />
          </Row>
        </>
      )}
    </>
  );
}

function IntroVideoControl() {
  const qc = useQueryClient();
  const { data, refetch } = useQuery<{ available: boolean; name: string | null }>({
    queryKey: ['intro', 'meta'],
    queryFn: () => api('/api/intro/meta'),
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function upload(file: File) {
    setBusy(true); setMsg(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('mdai_token');
      const res = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/intro/video`, {
        method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : undefined, body: fd,
      });
      if (!res.ok) throw new Error(String(res.status));
      setMsg('Uploaded ✓'); refetch(); qc.invalidateQueries({ queryKey: ['intro'] });
    } catch { setMsg('Upload failed'); } finally { setBusy(false); }
  }
  async function remove() {
    if (!window.confirm('Remove the intro video?')) return;
    setBusy(true); setMsg(null);
    try { await api('/api/intro/video', { method: 'DELETE' }); setMsg('Removed'); refetch(); }
    finally { setBusy(false); }
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {data?.available
        ? <span className="font-mono text-[11px] text-emerald-300/80">● {data.name ?? 'video set'}</span>
        : <span className="font-mono text-[11px] text-ink/40">none</span>}
      <label className="px-3 py-1.5 rounded-full border border-ink/20 cursor-pointer text-[11px] text-ink/70 hover:text-ink hover:border-ink/40">
        {busy ? '…' : data?.available ? 'Replace' : 'Upload'}
        <input type="file" accept="video/mp4,video/webm,video/*" className="hidden" disabled={busy}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = ''; }} />
      </label>
      {data?.available && (
        <button onClick={remove} disabled={busy} className="text-[11px] text-red-300/70 hover:text-red-300 px-1">remove</button>
      )}
      {msg && <span className="text-[11px] text-ink/50">{msg}</span>}
    </div>
  );
}

/* ============================ Documents ============================ */

interface StatsResp {
  counts: { memories: number; sessions: number; conversations: number; messages: number; news: number; users: number; activity: number };
  memoriesByVisibility: Record<string, number>;
  storage: { dbPath: string; dbBytes: number };
  uptimeMs: number;
}

function useStats() {
  return useQuery<StatsResp>({ queryKey: ['admin', 'stats'], queryFn: () => api<StatsResp>('/api/admin/stats'), refetchInterval: 10_000 });
}

// Documents pane lives in the Vault → Documents tab now.

/* ============================ Database ============================ */

function DatabasePane() {
  const { data, isLoading } = useStats();
  return (
    <>
      <PaneHead title="Database" sub="SQLite via Drizzle ORM. Single file, easy backup. pgvector swap-in planned for embeddings." />
      {isLoading || !data ? <Loader /> : (
        <>
          <Row label="Engine"><span className="font-mono text-[13px]">SQLite (better-sqlite3) · WAL</span> <StatusChip kind="live">live</StatusChip></Row>
          <Row label="File">
            <span className="font-mono text-[12px]">{data.storage.dbPath}</span>
            <span className="ml-2 font-mono text-[10.5px] text-ink/40">{formatBytes(data.storage.dbBytes)}</span>
          </Row>
          <Row label="Row counts">
            <ul className="space-y-1 font-mono text-[12px] text-ink/70">
              <li>memory <span className="text-ink/40">·</span> <span className="text-ink">{data.counts.memories}</span></li>
              <li>interview_session <span className="text-ink/40">·</span> <span className="text-ink">{data.counts.sessions}</span></li>
              <li>conversation <span className="text-ink/40">·</span> <span className="text-ink">{data.counts.conversations}</span></li>
              <li>message <span className="text-ink/40">·</span> <span className="text-ink">{data.counts.messages}</span></li>
              <li>news_item <span className="text-ink/40">·</span> <span className="text-ink">{data.counts.news}</span></li>
              <li>app_user <span className="text-ink/40">·</span> <span className="text-ink">{data.counts.users}</span></li>
              <li>activity_log <span className="text-ink/40">·</span> <span className="text-ink">{data.counts.activity}</span></li>
            </ul>
          </Row>
          <Row label="Memories by tier">
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(data.memoriesByVisibility).map(([tier, n]) => (
                <span key={tier} className="px-2 py-0.5 rounded-full border border-ink/15 text-[11.5px] text-ink/60">
                  {tier.replace('_', ' ')} · <span className="text-ink">{n}</span>
                </span>
              ))}
              {Object.keys(data.memoriesByVisibility).length === 0 && <span className="text-ink/40 italic font-serif">none yet</span>}
            </div>
          </Row>
          <Row label="Embeddings"><StatusChip kind="draft">token overlap (no vectors yet)</StatusChip></Row>
        </>
      )}
    </>
  );
}

/* ============================ Helpers ============================ */

function Stat({ n, label }: { n: number | string; label: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="font-serif text-3xl text-ink leading-none">{n}</span>
      <span className="text-[11px] text-ink/40">{label}</span>
    </div>
  );
}

function Loader() {
  return <div className="text-ink/40 text-[12px] py-8">Loading…</div>;
}

function formatBytes(n: number): string {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

function formatUptime(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (d > 0) return `${d}d ${h % 24}h`;
  if (h > 0) return `${h}h ${m % 60}m`;
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
}
